import "./Legal.scss";

export default function DataProcessingAgreement() {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Data Processing Agreement (DPA)</h1>
        <p className="last-updated">Last Updated: March 10, 2026</p>

        <section className="legal-section">
          <p>
            This Data Processing Agreement ("DPA") forms part of the{" "}
            <a href="/legal/terms-of-service">Terms of Service</a> and applies
            when you use SOMIGO for business purposes as an organization.
          </p>
          <p>
            <strong>Applicability:</strong> When the Customer is an organization
            using the Service for business purposes, the Customer acts as Data
            Controller and SOMIGO (Happyhill, based in Aarhus, Denmark) acts as
            Data Processor. For individual/personal accounts, SOMIGO acts as
            Data Controller and this DPA does not apply.
          </p>
          <p>
            This DPA governs the processing of Personal Data as defined under
            the General Data Protection Regulation (GDPR) and other applicable
            data protection laws.
          </p>
        </section>

        <section className="legal-section">
          <h2>1. Definitions</h2>
          <ul>
            <li>
              <strong>"Personal Data"</strong> means any information relating to
              an identified or identifiable natural person that is processed by
              SOMIGO on behalf of the Customer.
            </li>
            <li>
              <strong>"Data Controller"</strong> means the Customer, who
              determines the purposes and means of processing Personal Data.
            </li>
            <li>
              <strong>"Data Processor"</strong> means SOMIGO, who processes
              Personal Data on behalf of the Data Controller.
            </li>
            <li>
              <strong>"Data Subject"</strong> means the individual to whom
              Personal Data relates.
            </li>
            <li>
              <strong>"Sub-processor"</strong> means any third party engaged by
              SOMIGO to process Personal Data.
            </li>
            <li>
              <strong>"GDPR"</strong> means the General Data Protection
              Regulation (EU) 2016/679.
            </li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>2. Scope and Purpose of Processing</h2>
          <h3>2.1 Subject Matter</h3>
          <p>
            SOMIGO provides a project management and task organization platform
            that processes Personal Data on behalf of the Customer.
          </p>

          <h3>2.2 Nature and Purpose of Processing</h3>
          <p>SOMIGO processes Personal Data to:</p>
          <ul>
            <li>Provide the SOMIGO platform services to the Customer</li>
            <li>Store and manage tasks, projects, notes, and user data</li>
            <li>Enable collaboration between team members</li>
            <li>Process billing and subscription management</li>
            <li>Provide customer support</li>
            <li>Ensure platform security and performance</li>
          </ul>

          <h3>2.3 Duration of Processing</h3>
          <p>
            Personal Data will be processed for the duration of the Customer's
            active subscription and for a period of 30 days following account
            cancellation or termination, after which all data will be
            permanently deleted.
          </p>

          <h3>2.4 Types of Personal Data</h3>
          <p>SOMIGO may process the following categories of Personal Data:</p>
          <ul>
            <li>
              <strong>Account Information:</strong> Name, email address, job
              title
            </li>
            <li>
              <strong>User-Generated Content:</strong> Tasks, projects, notes,
              comments
            </li>
            <li>
              <strong>Usage Data:</strong> Login times, activity logs, feature
              usage
            </li>
            <li>
              <strong>Billing Information:</strong> Payment method details
              (processed via Stripe)
            </li>
            <li>
              <strong>Technical Data:</strong> IP addresses, browser type,
              device information
            </li>
          </ul>

          <h3>2.5 Categories of Data Subjects</h3>
          <p>
            Data Subjects include the Customer's employees, contractors, team
            members, and authorized users of the SOMIGO platform.
          </p>
        </section>

        <section className="legal-section">
          <h2>3. Data Processor Obligations</h2>
          <p>SOMIGO agrees to:</p>
          <ul>
            <li>
              Process Personal Data only on documented instructions from the
              Customer, including for transfers to third countries or
              international organizations
            </li>
            <li>
              Ensure that persons authorized to process Personal Data are bound
              by confidentiality obligations
            </li>
            <li>
              Implement appropriate technical and organizational measures to
              ensure data security (see Section 5)
            </li>
            <li>
              Respect the conditions for engaging Sub-processors (see Section 4)
            </li>
            <li>
              Assist the Customer in responding to Data Subject requests (see
              Section 7)
            </li>
            <li>
              Assist the Customer in ensuring compliance with GDPR obligations
            </li>
            <li>
              Delete or return all Personal Data after termination of services,
              unless retention is required by law
            </li>
            <li>
              Make available all information necessary to demonstrate compliance
              with GDPR obligations
            </li>
            <li>
              Notify the Customer without undue delay upon becoming aware of a
              Personal Data breach
            </li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. Sub-processors</h2>
          <h3>4.1 Authorized Sub-processors</h3>
          <p>
            The Customer acknowledges and agrees that SOMIGO may engage the
            following Sub-processors to process Personal Data:
          </p>

          <div className="subprocessor-table">
            <div className="subprocessor-row">
              <div className="subprocessor-name">
                <strong>DigitalOcean, LLC</strong>
              </div>
              <div className="subprocessor-purpose">
                Cloud hosting and infrastructure (EU data centers)
              </div>
              <div className="subprocessor-location">European Union</div>
            </div>
            <div className="subprocessor-row">
              <div className="subprocessor-name">
                <strong>Stripe, Inc.</strong>
              </div>
              <div className="subprocessor-purpose">
                Payment processing services
              </div>
              <div className="subprocessor-location">
                United States (GDPR-compliant with SCCs)
              </div>
            </div>
            <div className="subprocessor-row">
              <div className="subprocessor-name">
                <strong>Google LLC</strong>
              </div>
              <div className="subprocessor-purpose">
                Email delivery (Gmail SMTP) and website analytics
              </div>
              <div className="subprocessor-location">
                United States (GDPR-compliant with SCCs)
              </div>
            </div>
          </div>

          <h3>4.2 Changes to Sub-processors</h3>
          <p>
            SOMIGO will inform the Customer of any intended changes concerning
            the addition or replacement of Sub-processors via email notification
            at least 14 days in advance. The Customer may object to such changes
            by contacting{" "}
            <a href="mailto:contact@somigo.io">contact@somigo.io</a> within 7
            days of notification.
          </p>

          <h3>4.3 Sub-processor Obligations</h3>
          <p>
            SOMIGO ensures that all Sub-processors are bound by data protection
            obligations equivalent to those set out in this DPA, including
            appropriate technical and organizational security measures.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. Security Measures</h2>
          <p>
            SOMIGO implements the following technical and organizational
            measures to ensure data security:
          </p>

          <h3>5.1 Technical Measures</h3>
          <ul>
            <li>
              <strong>Encryption:</strong> Data encrypted in transit (TLS/SSL)
              and at rest (AES-256)
            </li>
            <li>
              <strong>Access Controls:</strong> Role-based access control (RBAC)
              and user authentication
            </li>
            <li>
              <strong>Network Security:</strong> Firewalls, intrusion detection,
              and DDoS protection
            </li>
            <li>
              <strong>Data Segregation:</strong> Logical separation of customer
              data
            </li>
            <li>
              <strong>Backups:</strong> Regular automated backups with secure
              storage
            </li>
          </ul>

          <h3>5.2 Organizational Measures</h3>
          <ul>
            <li>
              <strong>Access Management:</strong> Strict access controls and
              principle of least privilege
            </li>
            <li>
              <strong>Confidentiality:</strong> All personnel bound by
              confidentiality agreements
            </li>
            <li>
              <strong>Security Monitoring:</strong> Continuous monitoring and
              logging of security events
            </li>
            <li>
              <strong>Incident Response:</strong> Documented procedures for data
              breach response
            </li>
            <li>
              <strong>Vendor Management:</strong> Due diligence on all
              Sub-processors
            </li>
          </ul>

          <h3>5.3 Data Center Security</h3>
          <p>
            All infrastructure is hosted in European Union data centers operated
            by DigitalOcean, which maintain:
          </p>
          <ul>
            <li>ISO 27001 certification</li>
            <li>SOC 2 Type II compliance</li>
            <li>24/7 physical security and monitoring</li>
            <li>Redundant power and network connectivity</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>6. Data Subject Rights</h2>
          <p>
            SOMIGO will assist the Customer in fulfilling its obligations to
            respond to Data Subject requests, including:
          </p>
          <ul>
            <li>
              <strong>Right of Access:</strong> Provide access to Personal Data
            </li>
            <li>
              <strong>Right to Rectification:</strong> Correct inaccurate data
            </li>
            <li>
              <strong>Right to Erasure:</strong> Delete Personal Data ("right to
              be forgotten")
            </li>
            <li>
              <strong>Right to Data Portability:</strong> Export data in
              machine-readable format
            </li>
            <li>
              <strong>Right to Restriction:</strong> Limit processing of data
            </li>
            <li>
              <strong>Right to Object:</strong> Object to processing
            </li>
          </ul>
          <p>
            The Customer can exercise these rights through their SOMIGO account
            settings or by contacting{" "}
            <a href="mailto:contact@somigo.io">contact@somigo.io</a>. SOMIGO
            will respond to such requests within 30 days.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. Data Breach Notification</h2>
          <h3>7.1 Notification Timing</h3>
          <p>
            In the event of a Personal Data breach, SOMIGO will notify the
            Customer without undue delay and, where feasible, no later than 72
            hours after becoming aware of the breach.
          </p>

          <h3>7.2 Notification Content</h3>
          <p>The notification will include:</p>
          <ul>
            <li>Description of the nature of the breach</li>
            <li>Categories and approximate number of affected Data Subjects</li>
            <li>Categories and approximate number of Personal Data records</li>
            <li>Contact point for more information (contact@somigo.io)</li>
            <li>Likely consequences of the breach</li>
            <li>Measures taken or proposed to address the breach</li>
          </ul>

          <h3>7.3 Cooperation</h3>
          <p>
            SOMIGO will cooperate with the Customer and provide all necessary
            information to enable the Customer to fulfill its GDPR breach
            notification obligations to supervisory authorities and Data
            Subjects.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Data Transfers</h2>
          <h3>8.1 EU-Based Processing</h3>
          <p>
            All primary data processing and storage occurs within the European
            Union at DigitalOcean data centers in EU regions.
          </p>

          <h3>8.2 International Transfers</h3>
          <p>
            Limited Personal Data may be transferred to Sub-processors outside
            the EU (Stripe and Google in the United States). These transfers are
            protected by:
          </p>
          <ul>
            <li>
              Standard Contractual Clauses (SCCs) approved by the European
              Commission
            </li>
            <li>
              Supplementary measures ensuring adequate protection equivalent to
              GDPR
            </li>
            <li>GDPR-compliant data processing agreements with all parties</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>9. Data Retention and Deletion</h2>
          <h3>9.1 Retention Period</h3>
          <p>
            SOMIGO retains Personal Data only for as long as necessary to
            provide services to the Customer and fulfill the purposes outlined
            in this DPA.
          </p>

          <h3>9.2 Post-Termination</h3>
          <p>
            Upon termination or cancellation of the Customer's subscription:
          </p>
          <ul>
            <li>
              Data remains accessible to the Customer for 30 days to allow for
              data export
            </li>
            <li>
              After 30 days, all Personal Data is immediately deleted from
              SOMIGO's active production systems
            </li>
            <li>
              Backup copies may persist for up to 90 days in secure backup
              systems before being automatically overwritten. During this
              period, backup data is isolated and cannot be accessed or restored
              except as required by law
            </li>
            <li>
              The Customer can request immediate deletion of active data by
              contacting{" "}
              <a href="mailto:contact@somigo.io">contact@somigo.io</a>
            </li>
          </ul>

          <h3>9.3 Legal Retention</h3>
          <p>
            If retention is required by applicable law (e.g., tax, accounting,
            or regulatory requirements), SOMIGO will securely store the data for
            the legally mandated period and then delete it.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Audits and Compliance</h2>
          <h3>10.1 Information Access</h3>
          <p>
            SOMIGO will make available to the Customer all information necessary
            to demonstrate compliance with GDPR Article 28 obligations.
          </p>

          <h3>10.2 Audit Rights</h3>
          <p>
            SOMIGO will provide evidence of compliance with this DPA through:
          </p>
          <ul>
            <li>
              Providing relevant security documentation, policies, and
              procedures
            </li>
            <li>
              Sharing security certifications from our infrastructure providers
              (ISO 27001, SOC 2 Type II)
            </li>
            <li>
              Making available independent third-party audit reports where
              applicable
            </li>
          </ul>
          <p>
            If the above documentation is insufficient, the Customer may conduct
            an on-site audit or appoint an independent auditor, subject to:
          </p>
          <ul>
            <li>Reasonable advance notice (at least 30 days)</li>
            <li>Execution of a confidentiality agreement</li>
            <li>Scheduling during normal business hours</li>
            <li>Payment of reasonable costs associated with the audit</li>
            <li>No more than one audit per 12-month period</li>
          </ul>

          <h3>10.3 Security Certifications</h3>
          <p>
            SOMIGO maintains compliance with industry security standards through
            our infrastructure providers (DigitalOcean) who hold ISO 27001 and
            SOC 2 Type II certifications.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Liability and Indemnification</h2>
          <h3>11.1 Joint Liability</h3>
          <p>
            Each party will be liable to Data Subjects for damages caused by
            processing that infringes GDPR, in accordance with GDPR Article 82.
          </p>

          <h3>11.2 Limitation</h3>
          <p>
            SOMIGO's liability under this DPA is subject to the limitations set
            forth in the Terms of Service, except where such limitations are
            prohibited by applicable law.
          </p>

          <h3>11.3 Indemnification</h3>
          <p>
            Each party will indemnify the other for losses arising from its
            breach of this DPA, to the extent permitted by applicable law.
          </p>
        </section>

        <section className="legal-section">
          <h2>12. Term and Termination</h2>
          <h3>12.1 Term</h3>
          <p>
            This DPA takes effect on the date the Customer accepts the Terms of
            Service and continues until termination of the subscription.
          </p>

          <h3>12.2 Effect of Termination</h3>
          <p>Upon termination:</p>
          <ul>
            <li>
              SOMIGO will cease all processing of Personal Data except as
              necessary for data return or deletion
            </li>
            <li>The Customer may request export of all Personal Data</li>
            <li>
              All Personal Data will be deleted within 30 days unless otherwise
              requested
            </li>
          </ul>

          <h3>12.3 Survival</h3>
          <p>
            Sections relating to confidentiality, liability, and data deletion
            will survive termination of this DPA.
          </p>
        </section>

        <section className="legal-section">
          <h2>13. Amendments</h2>
          <p>
            SOMIGO may update this DPA from time to time to reflect changes in
            data protection laws, our processing activities, or business
            practices. Material changes will be communicated to Customers via
            email at least 30 days before taking effect.
          </p>
          <p>
            Continued use of the Service after changes take effect constitutes
            acceptance of the updated DPA.
          </p>
        </section>

        <section className="legal-section">
          <h2>14. Governing Law</h2>
          <p>
            This DPA is governed by the laws of Denmark and interpreted in
            accordance with the GDPR and other applicable EU data protection
            regulations.
          </p>
          <p>
            Any disputes relating to this DPA shall be subject to the exclusive
            jurisdiction of the courts of Aarhus, Denmark.
          </p>
        </section>

        <section className="legal-section">
          <h2>15. Contact Information</h2>
          <p>
            For questions about this Data Processing Agreement or data
            protection matters, please contact:
          </p>
          <ul className="contact-list">
            <li>
              <strong>Company:</strong> Happyhill
            </li>
            <li>
              <strong>CVR:</strong> 34028370
            </li>
            <li>
              <strong>Address:</strong> Topkærvej 7, 8200 Aarhus N, Denmark
            </li>
            <li>
              <strong>Email:</strong>{" "}
              <a href="mailto:contact@somigo.io">contact@somigo.io</a>
            </li>
            <li>
              <strong>Data Protection Inquiries:</strong> Contact{" "}
              <a href="mailto:contact@somigo.io">contact@somigo.io</a>
            </li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>16. Acceptance</h2>
          <p>
            By accepting the Terms of Service and using SOMIGO, the Customer
            acknowledges that they have read, understood, and agree to be bound
            by this Data Processing Agreement.
          </p>
          <p>
            This DPA is incorporated into and forms part of the Terms of Service
            between the Customer and SOMIGO.
          </p>
        </section>
      </div>
    </div>
  );
}
